import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import websocketService from '../services/websocket';
import { notificationsAPI } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);

  /**
   * Fetch notifications from backend
   */
  // Normalize a backend notification into the shape the UI renders, tolerating
  // field-name variations (title/subject, message/body/content, created_at/timestamp).
  const normalizeNotification = (n) => {
    if (!n || typeof n !== 'object') return n;
    return {
      ...n,
      _id: n._id || n.id,
      title: n.title || n.subject || n.heading || n.name || 'Notification',
      message: n.message || n.body || n.content || n.text || n.description || '',
      type: n.type || n.notification_type || n.category || 'system',
      created_at: n.created_at || n.timestamp || n.createdAt || n.created || null,
      is_read: n.is_read ?? n.read ?? false,
      read: n.read ?? n.is_read ?? false,
    };
  };

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await notificationsAPI.getNotifications();
      if (response) {
        const raw = response.data ?? response;
        const list = Array.isArray(raw)
          ? raw
          : (raw.notifications || raw.items || raw.data || []);
        const notifs = (Array.isArray(list) ? list : []).map(normalizeNotification);
        setNotifications(notifs);

        // Count unread notifications
        const unread = notifs.filter(n => !n.read && !n.is_read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
    }
  }, [isAuthenticated]);

  /**
   * Connect to WebSocket when user is authenticated
   */
  useEffect(() => {
    if (isAuthenticated && user) {
      const token = localStorage.getItem('authToken');
      const userID = user._id || user.id;

      if (token && userID) {
        websocketService.connect(token, userID);

        // Subscribe to WebSocket events
        const unsubscribeConnection = websocketService.on('connection', () => {
          setIsConnected(true);
        });

        const unsubscribeDisconnection = websocketService.on('disconnected', () => {
          setIsConnected(false);
        });

        // Subscribe to notification events
        const unsubscribeNotification = websocketService.on('notification', (data) => {
          addNotification(data);
        });

        const unsubscribeOrderUpdate = websocketService.on('order_update', (data) => {
          const notification = {
            type: 'order_update',
            title: 'Order Status Updated',
            message: `Your order #${data.order_number || data._id} status has been updated to ${data.status}`,
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        const unsubscribeNewMessage = websocketService.on('new_message', (data) => {
          const notification = {
            type: 'new_message',
            title: 'New Message',
            message: `You have a new message`,
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        const unsubscribePriceAlert = websocketService.on('price_alert', (data) => {
          const notification = {
            type: 'price_alert',
            title: 'Price Alert',
            message: `Price drop alert for ${data.product_name || 'product'}`,
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        const unsubscribePaymentUpdate = websocketService.on('payment_update', (data) => {
          const notification = {
            type: 'payment_update',
            title: 'Payment Update',
            message: `Payment ${data.status || 'updated'}`,
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        const unsubscribeWalletUpdate = websocketService.on('wallet_update', (data) => {
          const notification = {
            type: 'wallet_update',
            title: 'Wallet Update',
            message: `Wallet balance updated: ₦${data.balance?.toLocaleString() || '0'}`,
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        const unsubscribeProductUpdate = websocketService.on('product_update', (data) => {
          const notification = {
            type: 'product_update',
            title: 'Product Update',
            message: data.message || 'Product has been updated',
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        const unsubscribeDisputeUpdate = websocketService.on('dispute_update', (data) => {
          const notification = {
            type: 'dispute_update',
            title: 'Dispute Update',
            message: `Dispute status updated to ${data.status}`,
            data: data,
            timestamp: new Date().toISOString(),
            read: false
          };
          addNotification(notification);
        });

        // Fetch initial notifications
        fetchNotifications();

        // Cleanup on unmount
        return () => {
          unsubscribeConnection();
          unsubscribeDisconnection();
          unsubscribeNotification();
          unsubscribeOrderUpdate();
          unsubscribeNewMessage();
          unsubscribePriceAlert();
          unsubscribePaymentUpdate();
          unsubscribeWalletUpdate();
          unsubscribeProductUpdate();
          unsubscribeDisputeUpdate();
          websocketService.disconnect();
        };
      }
    } else {
      // Disconnect when user logs out
      websocketService.disconnect();
      setIsConnected(false);
      setNotifications([]);
      setUnreadCount(0);
    }
    // Depend on the user's id, not the `user` object itself — AuthContext
    // re-sets `user` to a fresh object (new reference, same underlying data)
    // once its background profile fetch resolves after the initial
    // cache-based login. Depending on the object caused this effect to
    // disconnect and immediately reconnect the WebSocket, often while the
    // first connection was still mid-handshake ("WebSocket is closed before
    // the connection is established").
  }, [isAuthenticated, user?._id || user?.id, fetchNotifications]);

  /**
   * Add new notification
   */
  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Show toast notification
    showToast(notification);
  };

  /**
   * Show toast notification
   */
  const showToast = (notification) => {
    setToastNotification(notification);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setToastNotification(null);
    }, 5000);
  };

  /**
   * Show custom toast message (for cart, shop actions, etc.)
   */
  const showCustomToast = ({ type = 'success', title, message, duration = 4000 }) => {
    const notification = {
      type,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false,
      isCustom: true
    };

    setToastNotification(notification);

    // Auto-hide after specified duration
    setTimeout(() => {
      setToastNotification(null);
    }, duration);
  };

  /**
   * Mark notification as read
   */
  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);

      setNotifications(prev =>
        prev.map(n => (n._id === notificationId || n.id === notificationId) ? { ...n, read: true, is_read: true } : n)
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      // Mark all unread notifications
      const unreadNotifications = notifications.filter(n => !n.read && !n.is_read);

      for (const notification of unreadNotifications) {
        await notificationsAPI.markAsRead(notification._id || notification.id);
      }

      setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
    }
  };

  /**
   * Delete notification
   */
  const deleteNotification = async (notificationId) => {
    try {
      await notificationsAPI.delete(notificationId);

      setNotifications(prev => prev.filter(n => (n._id !== notificationId && n.id !== notificationId)));

      // Decrease unread count if notification was unread
      const notification = notifications.find(n => (n._id === notificationId || n.id === notificationId));
      if (notification && !notification.read && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
    }
  };

  /**
   * Clear all notifications
   */
  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  /**
   * Dismiss toast notification
   */
  const dismissToast = () => {
    setToastNotification(null);
  };

  const value = {
    notifications,
    unreadCount,
    isConnected,
    toastNotification,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    dismissToast,
    fetchNotifications,
    showToast,
    showCustomToast
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
